import React from "react";
import { motion } from "framer-motion";
import {
  Settings, Clock, Wifi, Siren, Bot, MessageCircle,
  Utensils, Tag, Phone, Star, ArrowRight,
} from "lucide-react";
import { LangToggle } from "../components/Shared";
import { HOTEL_PHONE } from "../data";
import { translations } from "@/i18n";

const ICONS = { Clock, Wifi, Siren };

const MENU = [
  { key: "ai", icon: Bot },
  { key: "operator", icon: MessageCircle },
  { key: "menu", icon: Utensils },
  { key: "promo", icon: Tag },
  { key: "call", icon: Phone },
  { key: "feedback", icon: Star },
];

const container = { hidden: {}, visible: { transition: { staggerChildren: 0.06 } } };
const item = { hidden: { opacity: 0, y: 14 }, visible: { opacity: 1, y: 0, transition: { duration: 0.3 } } };

export default function Dashboard({ guest, navigate, onLogout, lang, setLang }) {
  const T = translations[lang];
  const fullName = `${guest.title} ${guest.name}`;

  const handleMenu = (key) => {
    if (key === "call") {
      window.location.href = `tel:${HOTEL_PHONE}`;
      return;
    }
    navigate(key);
  };

  return (
    <div data-testid="dashboard-screen">
      <div className="bg-gradient-to-r from-[#4AADDF] to-[#367ABD] text-white pt-safe px-6 pb-7 rounded-b-[24px] shadow-[0_4px_20px_rgba(74,173,223,0.25)] relative">
        <div className="absolute right-5 top-safe flex items-center gap-2 z-20">
          <LangToggle lang={lang} setLang={setLang} variant="light" />
          <button
            data-testid="settings-button"
            onClick={onLogout}
            className="w-10 h-10 flex items-center justify-center rounded-full active:bg-white/15 transition-colors"
            aria-label="Settings"
          >
            <Settings size={22} />
          </button>
        </div>
        <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }} className="pt-10">
          <h1 className="font-inter font-semibold text-[20px]">
            {T.greeting}{fullName} 👋
          </h1>
          <p className="font-inter text-[13px] text-white/85 mt-1">{T.roomInfo(guest.room)}</p>
        </motion.div>
      </div>

      <div className="px-4 -mt-3 relative z-10">
        <div className="flex gap-3 overflow-x-auto hide-scrollbar pb-1">
          {T.quickInfo.map((q, i) => {
            const Icon = ICONS[q.icon];
            return (
              <div key={i} className="flex items-center gap-2 bg-[#F0F7FF] border-l-[3px] border-[#C9A84C] rounded-[10px] px-3 py-2.5 shadow-[0_2px_10px_rgba(74,173,223,0.06)] whitespace-nowrap">
                <Icon size={16} className="text-[#4AADDF]" />
                <span className="text-[13px] text-[#1A1A2E] font-medium">{q.text}</span>
              </div>
            );
          })}
        </div>

        <motion.div variants={container} initial="hidden" animate="visible" className="grid grid-cols-2 gap-4 mt-5">
          {MENU.map((m) => {
            const Icon = m.icon;
            return (
              <motion.button
                key={m.key}
                variants={item}
                whileTap={{ scale: 0.97 }}
                data-testid={`menu-${m.key}`}
                onClick={() => handleMenu(m.key)}
                className="bg-white rounded-[16px] shadow-[0_2px_12px_rgba(74,173,223,0.08)] p-5 flex flex-col items-center text-center min-h-[120px] justify-center gap-3 border border-[#F0F7FF]"
              >
                <div className="w-[52px] h-[52px] rounded-full bg-[#E8F4FD] flex items-center justify-center">
                  <Icon size={26} className="text-[#4AADDF]" />
                </div>
                <span className="font-inter font-medium text-[14px] text-[#1A1A2E] leading-tight">{T.menuLabels[m.key]}</span>
              </motion.button>
            );
          })}
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4, duration: 0.4 }}
          className="bg-gradient-to-br from-[#F0F7FF] to-[#E8F4FD] rounded-[16px] p-5 mt-5 border border-[#E0E8F0]"
        >
          <h2 className="font-playfair font-semibold text-[19px] text-[#1A1A2E]">{T.welcomeTitle}</h2>
          <p className="text-[14px] text-[#6B7B8D] leading-relaxed mt-2">{T.welcomeDesc}</p>
          <button
            data-testid="view-full-info"
            onClick={() => navigate("promo")}
            className="inline-flex items-center gap-1.5 text-[14px] font-medium text-[#4AADDF] mt-3 active:opacity-70 transition-opacity"
          >
            {T.viewInfo} <ArrowRight size={16} />
          </button>
        </motion.div>
      </div>
    </div>
  );
}
